import React, {useEffect} from "react";
import Chart from "react-apexcharts";
import PropTypes from "prop-types";
import ShimmerLoader from "common/src/components/widget/ShimmerLoader";
import Fragment from "common/src/components/widget/Fragment";
import {useTranslation} from "react-i18next";
import {ChartTrendColors} from "../constants/chartColors";

const DedicatedBarChartWidget = (props) => {
  const {t} = useTranslation();
  const defaultXaxis = {
    type: "category",
    position: "bottom",
    padding: { top: 0, right: 10, bottom: 0, left: 10 },
    tickPlacement: "between",
    labels: {
      show: true,
      rotate: -60,
      rotateAlways: true,
      trim: false,
      hideOverlappingLabels: false,
      showDuplicates: true,
    },
  };

  const mergedXaxis = {
    ...defaultXaxis,
    ...(props?.options?.xaxis || {}),
    // deep-merge labels so caller can override rotate/trim/etc.
    labels: {
      ...(props?.options?.xaxis?.labels || defaultXaxis.labels ||{}),
      hideOverlappingLabels: false,
      hideDuplicates: false,
    },
    categories: props.xAxis,

  };

  const options = {

  ...{
    ...props.options,
    tooltip: {
      shared: true,
      intersect: false,
      hideEmptySeries: true,
      followCursor: false,
      y: {formatter: props?.dataLabelFunction},
      ...(props?.options?.tooltip || {}),
    },

    chart: {
      id: `dedicated-bar-chart-${props.id}`,
      stacked: true,
      toolbar: {
        show: true,
        enabled: true,
        tools: {
          download: true,
          selection: true,
          zoom: false,
          zoomIn: true,
          zoomOut: true,
          pan: true,
          reset: true
        },
      },
      zoom: {
        enabled: true,
        type: "y",
        autoScaleXAxis: true,
        autoScaleYAxis: false,
        allowMouseWheelZoom: false,
      },
      events: {
        mounted: () => setTimeout(() => addLegendTitle(), 300),
        updated: () => setTimeout(() => addLegendTitle(), 300),
        dataPointSelection: (event, chartCtx, config) => {
          try {
            const i = config?.dataPointIndex ?? -1;
            const s = config?.seriesIndex ?? 0;
            if (i < 0) return;

            const w = chartCtx?.w;
            const category =
                w?.globals?.categoryLabels?.[i] ??
                w?.config?.xaxis?.categories?.[i] ??
                (Array.isArray(props.xAxis) ? props.xAxis[i] : undefined);

            const value = w?.config?.series?.[s]?.data?.[i];

            const modifiers = {
              ctrlOrMeta: !!(event?.ctrlKey || event?.metaKey),
              shift: !!event?.shiftKey,
              alt: !!event?.altKey,
            };
            props?.onBarClick?.({ category, index: i, value, modifiers, event });
          } catch {}
        },
        },


    },
    states: {
      hover: {
        filter: {
          type: 'darken',
        }
      },
      active: {
        allowMultipleDataPointsSelection: true,
        filter: {
          type: 'darken',
        }
      },
    },
    xaxis: mergedXaxis,
    yaxis: {
      labels: { formatter: props.dataLabelFunction },
      ...(props?.options?.yaxis || {}),
    },
    dataLabels: {
      theme: "dark", // or "light"
      fillSeriesColor: true,
      enabled: true,
      hideOverflowingLabels: false,
      type: 'dark',
      style: {
        fontSize: "10px",
        fontWeight: 500
      },
     formatter: props.dataLabelFunction,
      ...(props?.options?.dataLabels || {}),
    },

    toolbar: {
      enabled: true,

      show: true,
      tools: {
        download: true,
        selection: true,
        zoom: true,
        zoomIn: true,
        zoomOut: true,
        pan: true,
        reset: true
      },
    },
    title: {
      text: props.title || undefined,
      align: "center",
    },
    colors: props.colors ?? [...ChartTrendColors],
    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "14px",
    },
    grid: {
      padding: {left: 50, right: 50, top: 15, bottom: 160},
      ...(props?.options?.grid || {}),
    },
    }
  };

  const addLegendTitle = () => {
    const chartId = `apexcharts-dedicated-bar-chart-${props.id}`;
    const chartContainer = document.querySelector(`#${chartId}`);
    const legendContainer = chartContainer?.querySelector(".apexcharts-legend");

    if (legendContainer && !chartContainer.querySelector(".legend-title")) {
      const titleDiv = document.createElement("div");
      titleDiv.className = "legend-title";
      titleDiv.textContent = t(props.seriesLegendTitle);
      titleDiv.style.fontWeight = "bold";
      titleDiv.style.marginRight = "10px";
      titleDiv.style.fontSize = "14px";
      legendContainer.prepend(titleDiv);
    }
  };

  useEffect(() => {
    addLegendTitle();
  }, [props.id]);

  return (
      <>

        <Fragment rendered={!props.isLoading}>
          <Fragment rendered={props.series?.length > 0}>
            <Chart
                width={"100%"}
                height={720}
                id={`apexcharts-dedicated-bar-chart-${props.id}`}
                options={options}
                series={props.series}
                type="bar"
            />

          </Fragment>
          <Fragment rendered={props.series?.length === 0}>
            <div className="chart-empty-container has-margin-20 has-min-height-300 is-flex flex-vertical flex-justify-center flex-align-items-center">
              <p>
                <i className="fz fz-empty-set extra-large has-text-grey-lighter"></i>
              </p>
              <p>{t("No data!")}</p>
            </div>
          </Fragment>
        </Fragment>
        <Fragment rendered={!!props.isLoading}>
          <div className="chart-loader-container has-margin-20">
            <ShimmerLoader/>
          </div>
        </Fragment>
      </>
  );
};

DedicatedBarChartWidget.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  title: PropTypes.string,
  xAxis: PropTypes.array,
  series: PropTypes.array,
  colors: PropTypes.array,
  isLoading: PropTypes.bool,
  dataLabelFunction: PropTypes.func,
  seriesLegendTitle: PropTypes.string,
  stacked: PropTypes.bool,
  options: PropTypes.object,
  onBarClick: PropTypes.func,          // ({ category, index, value, modifiers, event }) => void
  perPointFill: PropTypes.array        // string[] same length as xAxis (optional)
};

export default DedicatedBarChartWidget;
